import {
  FilterOptionKeys,
  GoalFilterDocument,
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import {
  FilterSectionDefinition,
  getGoalListFilters,
  getGoalListGroupOptions,
  getGoalListSortOptions,
  goalFilterSections,
  GoalListFilterKey,
  goalSortingModeTranslationKeys,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDeadline16 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors as IconButtonColors } from 'shared/components/ui/icon-button';
import { useUser } from 'shared/hooks/use-user';

export type GoalListFilterButtonProps = {
  filterDocument: GoalFilterDocument;
  defaultFilterDocument?: GoalFilterDocument;
  hideLifeArea?: boolean;
  // per-surface overrides; the defaults cover the standard goal list
  // GoalType rides along for the Timeline, the one goal list that offers it
  supportedFilters?: (GoalListFilterKey | FilterOptionKeys.GoalType)[];
  supportedGroupByModes?: GoalGroupBy[];
  supportedSortingModes?: GoalSortingMode[];
  filterLabelOverrides?: Partial<Record<FilterOptionKeys, string>>;
  filterInfoUrlOverrides?: Partial<Record<FilterOptionKeys, string | null>>;
  // goal lists that also show projects pass the goals-and-projects layout
  filterSections?: FilterSectionDefinition[];
  iconColor?: IconButtonColors;
  onUpdate: (partial: Partial<GoalFilterDocument>) => void;
  onReset: () => void;
};

export const GoalListFilterButton: React.FC<GoalListFilterButtonProps> = ({
  filterDocument,
  defaultFilterDocument,
  hideLifeArea,
  supportedFilters,
  supportedGroupByModes,
  supportedSortingModes,
  filterLabelOverrides,
  filterInfoUrlOverrides,
  filterSections = goalFilterSections,
  iconColor,
  onUpdate,
  onReset,
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const tags = useSortedTags();

  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const handleSortDirectionChange = (direction: OrderDirection) =>
    updateUserSettings({ goalSortDirection: direction });

  // The goal "Deadline" group reuses the task group-by key labelled "Date".
  const labelOverrides = useMemo(
    () => ({
      [GroupByMode.Deadline]: t('general.labels.goals.sort.options.deadline'),
    }),
    [t],
  );
  // The goal "Date" filter targets a goal's start date.
  const mergedFilterLabelOverrides = useMemo(
    () => ({
      [FilterOptionKeys.Date]: t('general.labels.start-date'),
      ...filterLabelOverrides,
    }),
    [t, filterLabelOverrides],
  );
  const filterIconOverrides = useMemo(
    () => ({ [FilterOptionKeys.Deadline]: CalendarDeadline16 }),
    [],
  );

  return (
    <FilterButton
      filterDocument={filterDocument}
      defaultFilterDocument={defaultFilterDocument}
      goals={goals}
      lifeAreas={lifeAreas}
      tags={tags}
      updateFilterDocument={onUpdate}
      supportedGroupByModes={
        supportedGroupByModes ?? getGoalListGroupOptions({ hideLifeArea })
      }
      supportedSortingModes={
        supportedSortingModes ?? getGoalListSortOptions({ hideLifeArea })
      }
      supportedFilters={
        supportedFilters ?? getGoalListFilters({ hideLifeArea })
      }
      sortingTranslationKeys={goalSortingModeTranslationKeys}
      sortDirection={sortDirection}
      onSortDirectionChange={handleSortDirectionChange}
      labelOverrides={labelOverrides}
      filterLabelOverrides={mergedFilterLabelOverrides}
      filterIconOverrides={filterIconOverrides}
      filterInfoUrlOverrides={filterInfoUrlOverrides}
      filterSections={filterSections}
      title={t('context-menus.filtering.goal.title')}
      onReset={onReset}
      preventDrag
      renderAsIconButton
      iconColor={iconColor}
    />
  );
};
